import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { formatEventDates } from "../utils/dateUtils";
import EventRow from "../components/EventRow";
import slugify from "../utils/slugify";
import { SeasonNoticeAndNewsletter } from "../components/SeasonNoticeAndNewsletter";
import Breadcrumb from "../components/Breadcrumb";
import { useTranslation } from "react-i18next";

function OperaHousePage() {
  const { operaHouseName } = useParams();
  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venueError, setVenueError] = useState(null);
  const [eventsError] = useState(null);
  const { t } = useTranslation();

  const toTitleCase = (str) =>
    str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const italianCityMap = {
    Milan: "Milano",
    Naples: "Napoli",
    Rome: "Roma",
    Venice: "Venezia",
    Verona: "Verona",
    Lajatico: "Lajatico",
  };

  const germanCityMap = {
    Munich: "München",
    Berlin: "Berlin",
    Dresden: "Dresden",
    Bayreuth: "Bayreuth",
  };

  const austrianCityMap = {
    Vienna: "Wien",
    Salzburg: "Salzburg",
    Bregenz: "Bregenz",
  };

  const getLocalizedWelcome = (country, city) => {
    const greetings = {
      Germany: `Willkommen in ${germanCityMap[city] || toTitleCase(city)}`,
      Spain: `Bienvenido a ${toTitleCase(city)}`,
      France: `Bienvenue à ${toTitleCase(city)}`,
      Italy: `Benvenuti a ${italianCityMap[city] || toTitleCase(city)}`,
      Austria: `Willkommen in ${austrianCityMap[city] || toTitleCase(city)}`,
      Norway: `Velkommen til ${toTitleCase(city)}`,
      Russia: `Dobro pozhalovat v ${toTitleCase(city)}`,
      "United States": `Welcome to ${toTitleCase(city)}`,
      "United Kingdom": `Welcome to ${toTitleCase(city)}`,
    };

    return greetings[country] || `Welcome to ${toTitleCase(city)}`;
  };

  const fetchVenues = React.useCallback(async () => {
    try {
      const res = await axios.get("/api/venues");
      const foundVenue = res.data.find(
        (v) => slugify(v.name) === operaHouseName
      );
      const resEvents = await axios.get(`/api/events?venue=${foundVenue._id}`);
      setEvents(resEvents.data);
      setVenue(foundVenue);
      setVenueError(null);
    } catch (err) {
      setVenueError(t("OperaHousePage.error"));
    }
  }, [operaHouseName]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchVenues();
      setLoading(false);
    };
    fetchData();
  }, [operaHouseName, fetchVenues]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [operaHouseName]);

  const handleRetry = (type) => {
    setLoading(true);
    fetchVenues();
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-opera-red mx-auto"></div>
          <p className="mt-4 text-black">{t("OperaHousePage.loading")}</p>
        </div>
      </div>
    );
  }

  if (venueError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{venueError}</p>
          <button
            onClick={() => handleRetry("venue")}
            className="bg-opera-red text-white px-4 py-2 rounded hover:bg-opera-red-dark transition-colors"
          >
            {t("OperaHousePage.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!venue || !venue.published) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-black">{t("OperaHousePage.notFound")}</p>
        </div>
      </div>
    );
  }
  const countryCodes = {
    France: "FR",
    Germany: "DE",
    Italy: "IT",
    Austria: "AT",
    "United States": "US",
    "United Kingdom": "GB",
  };
  const countryCode = countryCodes[venue?.country] || venue?.country;

  // Helper to normalize URLs to absolute
  function normalizeUrl(url) {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `https://operatoday.org${url}`;
    return `https://operatoday.org/${url}`;
  }

  const heroImage = venue?.coverImage || "/images/default-opera.jpg";
  const descriptionImage = venue?.operaImage || "/images/default-opera.jpg";
  const venueName = venue?.name || "Opera House";
  const venueCity = venue?.cityNew?.name || "";
  const venueCountry = venue?.country || "";
  const venueAddress =
    venue?.streetAddress === "undefined" ? "" : venue?.streetAddress || "";
  const postalCode = venue?.postalCode || "";
  const venueDescription = venue?.description || "";
  const venueShortDescription =
    venue?.shortDescription === "undefined"
      ? ""
      : venue?.shortDescription || "";

  const marginTopClass = venueShortDescription.length > 116 ? "mt-14" : "mt-7";

  return (
    <div className="bg-white">
      {/* Schema.org PerformingArtsTheater JSON-LD for Venue */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            (() => {
              const data = {
                "@context": "https://schema.org",
                "@type": "PerformingArtsTheater",
                name: venueName,
                ...(heroImage ? { image: normalizeUrl(heroImage) } : {}),
                ...(venueDescription ? { description: venueDescription } : {}),
              };
              if (venueAddress || venueCity || venueCountry) {
                data.address = {
                  "@type": "PostalAddress",
                  ...(venueAddress ? { streetAddress: venueAddress } : {}),
                  ...(venueCity ? { addressLocality: venueCity } : {}),
                  ...(venueCountry ? { addressCountry: countryCode } : {}),
                };
              }
              return data;
            })()
          ),
        }}
      />
      {/* Hero Section */}
      <div className="relative w-full h-[180px] md:h-[32vw] lg:h-[25vw]  overflow-hidden">
        <img
          src={heroImage}
          alt={venueName}
          className="w-full h-full object-cover"
        />
        <div className="absolute mt-20 inset-0 flex flex-col items-center justify-center p-4 text-center">
          <h1
            className="text-3xl md:text-5xl lg:text-6xl mb-2 text-white font-cormorant-garamond font-medium"
            style={{
              textShadow:
                "2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.6), 0 0 8px rgba(0,0,0,0.5)",
            }}
          >
            {getLocalizedWelcome(venueCountry, venueCity)}
          </h1>
        </div>
      </div>
      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          {
            href: "/opera-houses-and-festivals",
            label: "Destinations",
          },
          {
            href: "#",
            label: venueName,
          },
        ]}
      />
      {/* Main Content */}
      <main className="max-w-[850px] lg:max-w-[1000px] xl:max-w-[1280px] mx-auto px-4 md:px-8 py-8">
        <section className="mb-8">
          <h1 className="text-3xl text-left mb-4 capitalize">{venueName}</h1>
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="w-full lg:w-[78%]">
              <div className="text-black text-lg text-justify mb-8">
                {venueShortDescription && <p>{venueShortDescription}</p>}
                {(!venueShortDescription || venueDescription === "") && (
                  <p className="mb-[60px]"></p>
                )}
              </div>
              <div
                className="text-lg text-black leading-relaxed text-justify pros"
                dangerouslySetInnerHTML={{ __html: venueDescription }}
              />
            </div>
            <div className="w-full lg:w-[23%] -mt-3 hidden lg:block">
              <div className="flex -ml-3 items-center mb-4">
                <img
                  src="/images/venue.jpg"
                  alt="Venue"
                  width="50"
                  height="50"
                />
                <div>
                  <p
                    className="text-base text-black"
                    style={{ margin: "0", padding: "0", lineHeight: "1.2" }}
                  >
                    {[
                      venueAddress,
                      postalCode,
                      toTitleCase(venueCity),
                      countryCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
              <div
                className={`w-full max-w-[260px] aspect-square ${marginTopClass}`}
              >
                {descriptionImage ? (
                  <img
                    src={descriptionImage}
                    alt={venueName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white flex items-center justify-center text-black text-base rounded-lg"></div>
                )}
              </div>
            </div>
          </div>
        </section>
        <section>
          <h2 className="text-2xl font-cinzel text-center md:text-left mb-4 capitalize">
            {t("OperaHousePage.upcomingPerformances")}
          </h2>
          {eventsError ? (
            <div className="text-center">
              <p className="text-black">{eventsError}</p>
              <button
                onClick={() => handleRetry("events")}
                className="mt-4 bg-[#8C0000] text-white px-4 py-2 text-sm hover:bg-[#B20000] transition-colors"
              >
                {t("OperaHousePage.retry")}
              </button>
            </div>
          ) : events.length === 0 ? (
            <p className="text-black text-center italic mb-10">
              {t("OperaHousePage.noEvents")}
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-w-[1240px] mx-auto my-6">
              {events.map((event) => (
                <EventRow
                  key={event._id}
                  event={event}
                  formatEventDates={formatEventDates}
                  page="operahouse"
                  pageImage={venue?.operaImage || venue?.coverImage}
                />
              ))}
            </div>
          )}
          <SeasonNoticeAndNewsletter />
        </section>
      </main>
    </div>
  );
}

export default OperaHousePage;
